// PixiJS (WebGL) renderer — Track A, Faz 1 (slice 1: the REALISTIC ocean on the GPU).
//
// Rebuilt from the old pastel prototype to match the CURRENT art direction: the same seamless fractal
// water tile the Canvas2D path uses (passed in from the game), two counter-drifting parallax layers,
// a time-of-day palette wash (day → sunset → night → dawn, driven per frame by the game's palette),
// additive sun glints replacing the expensive per-pixel water field, and a night starfield.
//
// It stands up a WebGL canvas BEHIND the existing 2D game canvas; the 2D canvas keeps drawing the
// world/units/HUD on top (transparent where the sea used to be). Layered, incremental migration —
// the sea is the whole background fill cost, so this alone moves the biggest per-frame 2D burden
// (two full-screen pattern fills + gradient wash + the per-pixel shimmer buffer) onto the GPU.
//
// High-level Pixi objects only (Sprite / TilingSprite / Texture) — no custom GLSL — so there is no
// shader-compile risk. The game's own rAF drives rendering (Pixi's ticker is stopped).
import { Application, Container, Graphics, Sprite, Texture, TilingSprite } from 'pixi.js';

// Additive glint speckle — many faint cream-white sparks that scroll with the world; two layers of
// this replace the Canvas2D per-pixel water field at a fraction of the cost.
function makeGlintTexture(): Texture {
  const S = 512;
  const cv = document.createElement('canvas');
  cv.width = S; cv.height = S;
  const c = cv.getContext('2d')!;
  let seed = 1234567;
  const rng = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
  // SPARSE but LIVING — real sun glitter is a scatter of bright pinpricks, not a uniform snow. Brighter
  // and a touch larger than the first cut, which was so faint it read as a matte/flat sea (QA finding).
  for (let i = 0; i < 170; i++) {
    const x = rng() * S, y = rng() * S, r = 0.5 + rng() * 2.0, a = 0.14 + rng() * 0.34;
    const rg = c.createRadialGradient(x, y, 0, x, y, r);
    rg.addColorStop(0, `rgba(255,251,240,${a})`);
    rg.addColorStop(1, 'rgba(255,251,240,0)');
    c.fillStyle = rg; c.beginPath(); c.arc(x, y, r, 0, 6.2832); c.fill();
  }
  return Texture.from(cv);
}

// Sun-reflection sheen — a broad soft warm glow biased toward the NW sun, laid over the sea (screen-space,
// additive). This is the DIRECTIONAL sunlight on the water that a uniform speckle can't give; it stops the
// ocean reading as a flat matte sheet (QA #5). Baked once as a soft radial gradient, tinted/faded per frame.
function makeSheenTexture(): Texture {
  const S = 512;
  const cv = document.createElement('canvas');
  cv.width = S; cv.height = S;
  const c = cv.getContext('2d')!;
  const g = c.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, 'rgba(255,247,226,0.9)');
  g.addColorStop(0.45, 'rgba(255,244,220,0.32)');
  g.addColorStop(1, 'rgba(255,244,220,0)');
  c.fillStyle = g; c.fillRect(0, 0, S, S);
  return Texture.from(cv);
}

// Night starfield tile — pin-prick stars with a few brighter ones (screen-space, additive, faded by night).
function makeStarTexture(): Texture {
  const S = 512;
  const cv = document.createElement('canvas');
  cv.width = S; cv.height = S;
  const c = cv.getContext('2d')!;
  let seed = 424242;
  const rng = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
  // density calibrated to the Canvas2D starfield (~150 stars on a 1080p screen): the 512² tile
  // repeats ~8× across a screen, so it carries only ~20 stars — the first cut had 150/tile and
  // buried the night sea under white dots (user feedback)
  for (let i = 0; i < 20; i++) {
    const x = rng() * S, y = rng() * S, r = 0.4 + rng() * 1.1, a = 0.25 + rng() * 0.55;
    c.fillStyle = `rgba(255,253,242,${a})`;
    c.beginPath(); c.arc(x, y, r, 0, 6.2832); c.fill();
  }
  return Texture.from(cv);
}

export class PixiRenderer {
  private app: Application;
  private tile1!: TilingSprite;  // fractal water, near layer (matches 2D: scale 1.7, α 1)
  private tile2!: TilingSprite;  // far layer, counter-drift (scale 2.9, α 0.32)
  private wash!: Sprite;         // time-of-day palette wash over the texture
  private sheen!: Sprite;        // broad NW sun-reflection glow on the sea (directional sunlight)
  private glint1!: TilingSprite; // additive sun glints (the GPU water-field replacement)
  private glint2!: TilingSprite;
  private stars!: TilingSprite;  // night sky
  private storm!: Sprite;        // weather-reactive sea: dark storm tint (rain/snow) / warm dust tint
  private frost!: Sprite;        // freeze-over: the sea itself pales as the world ices up
  private chop!: TilingSprite;   // churned surface racing across the water while a storm blows
  private floor!: Container;     // Faz-1 slice 2: WORLD-SPACE floor content (depth blobs, pack ice) —
                                 // everything here sits below the entire 2D world by construction
  private floorMap = new Map<string, Sprite>();
  // Faz-1 FINAL STEP (gl-world-plan.md): the WORLD stage — animated world-space content migrating
  // off the 2D canvas bottom-up in paint order (?glworld=1). Pooled sprites (mark-and-sweep per
  // frame) + per-frame-rebuilt Graphics, camera+TILT transform mirrored from the 2D world pass.
  private world!: Container;
  private worldMap = new Map<string, Sprite>();
  private worldSeen = new Set<string>();
  private worldGfx = new Map<string, Graphics>();
  private worldGfxSeen = new Set<string>();
  private washBucket = -2;       // palette bucket the wash gradient was last baked at
  private ready = false;

  private constructor() { this.app = new Application(); }

  /** Create the WebGL canvas behind `gameCanvas`. `oceanTile` is the game's seamless 512² water tile. */
  static async create(gameCanvas: HTMLCanvasElement, oceanTile: HTMLCanvasElement): Promise<PixiRenderer> {
    const self = new PixiRenderer();
    const canvas = document.createElement('canvas');
    canvas.id = 'pixi-bg';
    // Positive stacking (avoids negative-z compositing quirks): pixi(1) < #game(2) < HUD(5+).
    canvas.style.cssText = 'position:fixed;left:0;top:0;display:block;z-index:1;';
    gameCanvas.parentElement!.insertBefore(canvas, gameCanvas);
    gameCanvas.style.zIndex = '2'; // 2D world/units/HUD paint above the WebGL ocean

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    await self.app.init({
      canvas,
      resolution: dpr,
      autoDensity: true,        // CSS size = window, backing store = window × dpr
      resizeTo: window,
      antialias: true,
      backgroundColor: 0x0a2238, // deep ocean under everything (matches the realistic sea floor tone)
      preference: 'webgl',
    });
    self.app.ticker.stop();     // the GAME's rAF drives rendering; we call app.render() ourselves

    const W = window.innerWidth, H = window.innerHeight;
    const waterTex = Texture.from(oceanTile);
    self.tile1 = new TilingSprite({ texture: waterTex, width: W, height: H });
    self.app.stage.addChild(self.tile1);
    self.tile2 = new TilingSprite({ texture: waterTex, width: W, height: H });
    self.tile2.alpha = 0.32;
    self.app.stage.addChild(self.tile2);
    self.wash = new Sprite(Texture.WHITE); // real gradient texture is baked on the first render()
    self.app.stage.addChild(self.wash);
    // WEATHER-REACTIVE SEA: a storm visibly darkens the water (dust warms it), a freeze pales it,
    // and wind-churned chop races across the surface — the ocean itself lives with the weather
    self.storm = new Sprite(Texture.WHITE); self.storm.alpha = 0;
    self.app.stage.addChild(self.storm);
    self.frost = new Sprite(Texture.WHITE); self.frost.tint = 0xcfe1ef; self.frost.alpha = 0;
    self.app.stage.addChild(self.frost);
    const glintTex = makeGlintTexture();
    self.chop = new TilingSprite({ texture: waterTex, width: W, height: H }); // storm chop reuses the water tile at a tight scale
    self.chop.alpha = 0;
    self.app.stage.addChild(self.chop);
    self.sheen = new Sprite(makeSheenTexture()); // broad NW sun-reflection glow (sits under the sparkle glints)
    self.sheen.anchor.set(0.5); self.sheen.blendMode = 'add'; self.sheen.alpha = 0;
    self.app.stage.addChild(self.sheen);
    self.glint1 = new TilingSprite({ texture: glintTex, width: W, height: H });
    self.glint1.blendMode = 'add';
    self.app.stage.addChild(self.glint1);
    self.glint2 = new TilingSprite({ texture: glintTex, width: W, height: H });
    self.glint2.blendMode = 'add';
    self.app.stage.addChild(self.glint2);
    self.stars = new TilingSprite({ texture: makeStarTexture(), width: W, height: H });
    self.stars.blendMode = 'add'; self.stars.alpha = 0;
    self.app.stage.addChild(self.stars);
    self.floor = new Container();
    self.floor.sortableChildren = true; // painter's order via zIndex: depth(0) < ice(10) < island stack(20+)
    self.app.stage.addChild(self.floor);
    self.world = new Container();
    self.world.sortableChildren = true; // paint order via zIndex (froth 15 < rock shadow 16 < … < body 18)
    self.app.stage.addChild(self.world); // above the floor, still below the whole 2D canvas

    self.ready = true;
    return self;
  }

  // ---- world stage (Faz-1 final step) ----
  /** Begin a world-stage frame: everything not touched again this frame is hidden at worldEnd(). */
  worldBegin(): void {
    if (!this.ready) return;
    this.worldSeen.clear();
    this.worldGfxSeen.clear();
  }
  /** Pooled world-space sprite (world coords, centre anchor). Textures are cached by source canvas. */
  worldSprite(key: string, src: HTMLCanvasElement, x: number, y: number, w: number, h: number,
              opts?: { rot?: number; alpha?: number; z?: number }): void {
    if (!this.ready) return;
    let sp = this.worldMap.get(key);
    if (!sp) {
      sp = new Sprite(Texture.from(src));
      sp.anchor.set(0.5);
      this.worldMap.set(key, sp);
      this.world.addChild(sp);
    }
    this.worldSeen.add(key);
    sp.visible = true;
    sp.position.set(x, y);
    sp.width = w; sp.height = h;
    sp.rotation = opts?.rot ?? 0;
    sp.alpha = opts?.alpha ?? 1;
    sp.zIndex = opts?.z ?? 0;
  }
  /** Per-frame world-space Graphics layer: cleared on first request each frame, rebuilt by the caller. */
  worldGraphics(key: string, z = 0): Graphics | null {
    if (!this.ready) return null;
    let g = this.worldGfx.get(key);
    if (!g) { g = new Graphics(); g.zIndex = z; this.worldGfx.set(key, g); this.world.addChild(g); }
    if (!this.worldGfxSeen.has(key)) { this.worldGfxSeen.add(key); g.clear(); g.visible = true; }
    return g;
  }
  /** End a world-stage frame: hide pooled content that was not re-touched (culled / despawned). */
  worldEnd(): void {
    if (!this.ready) return;
    for (const [key, sp] of this.worldMap) if (!this.worldSeen.has(key)) sp.visible = false;
    for (const [key, g] of this.worldGfx) if (!this.worldGfxSeen.has(key)) g.visible = false;
  }
  /** Destroy pooled world sprites by key prefix (new match layout). Textures are SHARED bakes — kept. */
  clearWorldByPrefix(prefix: string): void {
    for (const [key, sp] of this.worldMap) {
      if (!key.startsWith(prefix)) continue;
      this.worldMap.delete(key);
      this.world.removeChild(sp);
      sp.destroy(); // texture stays in the cache — rock variants etc. are shared across sprites
    }
  }

  /** Add-or-update a world-space floor sprite (world coords; the container applies camera + tilt). */
  upsertFloor(key: string, src: HTMLCanvasElement, x: number, y: number, w: number, h: number, alpha: number, z = 0): void {
    if (!this.ready) return;
    let sp = this.floorMap.get(key);
    if (!sp) { sp = new Sprite(Texture.from(src)); sp.zIndex = z; this.floorMap.set(key, sp); this.floor.addChild(sp); }
    sp.visible = true;
    sp.position.set(x, y);
    sp.width = w; sp.height = h;
    sp.alpha = alpha;
  }
  /** Hide a floor sprite (e.g., pack ice after the thaw) without destroying its texture. */
  hideFloor(key: string): void {
    const sp = this.floorMap.get(key);
    if (sp) sp.visible = false;
  }
  /** Destroy all floor sprites whose key starts with `prefix` (new match → new island layout). */
  clearFloorByPrefix(prefix: string): void {
    for (const [key, sp] of this.floorMap) {
      if (!key.startsWith(prefix)) continue;
      this.floorMap.delete(key);
      this.floor.removeChild(sp);
      sp.destroy({ texture: true }); // island bakes are per-layout — free the GPU memory too
    }
  }

  /** Bake the time-of-day wash gradient (only when the palette bucket changes — not per frame). */
  private bakeWash(sea: readonly [string, string, string]) {
    const cv = document.createElement('canvas');
    cv.width = 4; cv.height = 256;
    const c = cv.getContext('2d')!;
    const g = c.createLinearGradient(0, 0, 0, 256);
    g.addColorStop(0, sea[0]); g.addColorStop(0.5, sea[1]); g.addColorStop(1, sea[2]);
    c.fillStyle = g; c.fillRect(0, 0, 4, 256);
    const old = this.wash.texture;
    this.wash.texture = Texture.from(cv);
    if (old !== Texture.WHITE) old.destroy(true); // no texture leak across the day cycle
  }

  /**
   * Draw the ocean for this frame, locked to the game camera. `sea`/`night` come from the game's
   * time-of-day palette; `bucket` identifies the palette state (rebake the wash only when it changes).
   */
  render(camX: number, camY: number, zoom: number, time: number, W: number, H: number,
         sea: readonly [string, string, string], night: number, bucket: number, tilt = 1,
         wxKind: string | null = null, wxLevel = 0, freeze = 0): void {
    if (!this.ready) return;
    if (bucket !== this.washBucket) { this.washBucket = bucket; this.bakeWash(sea); }
    // weather on the WATER: storms darken it (dust warms it), hard weather churns it, a freeze pales it
    this.storm.width = W; this.storm.height = H;
    if (wxKind === 'dust') { this.storm.tint = 0x8a6c3c; this.storm.alpha = wxLevel * 0.12; }
    else { this.storm.tint = 0x0a141e; this.storm.alpha = wxKind === 'rain' ? wxLevel * 0.2 : wxKind === 'snow' ? wxLevel * 0.12 : 0; }
    this.frost.width = W; this.frost.height = H;
    this.frost.alpha = freeze * 0.16;
    this.chop.width = W; this.chop.height = H;
    const chopA = wxKind === 'rain' ? wxLevel * 0.16 : wxKind === 'snow' ? wxLevel * 0.08 : 0;
    this.chop.alpha = chopA;
    if (chopA > 0.005) { // wind-driven churn: tight tiles racing diagonally across the surface
      this.chop.tileScale.set(zoom * 4.4);
      this.chop.tilePosition.set(W / 2 - (camX + time * 130) * zoom, H / 2 - (camY + time * 88) * zoom);
    }
    // world-space floor + world stage: mirror the 2D world transform exactly (incl. the 2.5D Y squash)
    this.floor.position.set(W / 2 - camX * zoom, H / 2 - camY * zoom * tilt);
    this.floor.scale.set(zoom, zoom * tilt);
    this.world.position.set(W / 2 - camX * zoom, H / 2 - camY * zoom * tilt);
    this.world.scale.set(zoom, zoom * tilt);
    // exact mirror of the Canvas2D drawOcean: two layers, world-locked, gently counter-drifting
    const layers: [TilingSprite, number, number, number][] = [[this.tile1, 1.7, 8, 5], [this.tile2, 2.9, -12, -7]];
    for (const [sp, scale, dx, dy] of layers) {
      sp.width = W; sp.height = H;
      const sc = zoom * scale;
      sp.tileScale.set(sc);
      sp.tilePosition.set(-(camX + time * dx) * zoom + W / 2, -(camY + time * dy) * zoom + H / 2);
    }
    this.wash.width = W; this.wash.height = H;
    this.wash.alpha = Math.min(1, 0.52 + night * 0.3); // heavier wash at night (mirror drawOcean)
    // sun-reflection SHEEN: a broad soft warm glow anchored toward the NW sun (screen-space, like real
    // sun glare on water) — the directional sunlight that keeps the sea from reading as a flat matte sheet
    this.sheen.position.set(W * 0.32, H * 0.30);
    this.sheen.width = Math.max(W, H) * 1.25; this.sheen.height = Math.max(W, H) * 1.05;
    this.sheen.alpha = Math.max(0, (0.10 + Math.sin(time * 0.4) * 0.012) * (1 - night)); // day-only, gentle breathe
    // sun glints: two world-locked parallax layers, calmer at night (the sun is what sparkles)
    const sx = W / 2 - camX * zoom, sy = H / 2 - camY * zoom;
    const glintA = 0.15 * (1 - night); // visible sun-glitter by day, GONE at night (was 0.05 → read matte/flat, QA)
    this.glint1.width = W; this.glint1.height = H;
    this.glint1.tileScale.set(zoom * 1.15);
    this.glint1.tilePosition.set(sx + Math.sin(time * 0.12) * 6, sy + Math.cos(time * 0.1) * 6);
    this.glint1.alpha = Math.max(0, glintA + Math.sin(time * 0.6) * 0.035); // livelier twinkle
    this.glint2.width = W; this.glint2.height = H;
    this.glint2.tileScale.set(zoom * 1.9);
    this.glint2.tilePosition.set(sx * 0.6 - Math.sin(time * 0.08) * 5, sy * 0.6 + Math.cos(time * 0.07) * 5);
    this.glint2.alpha = Math.max(0, glintA * 0.62 + Math.cos(time * 0.5) * 0.03);
    // stars fade in after dusk, twinkling gently as one field (screen-space, slow drift)
    this.stars.width = W; this.stars.height = H;
    const starA = Math.max(0, Math.min(1, (night - 0.05) / 0.55)) * 0.55;
    this.stars.alpha = starA * (0.82 + 0.18 * Math.sin(time * 1.7));
    this.stars.tilePosition.set(-time * 1.2, time * 0.5);
    this.app.render();
  }

  destroy(): void {
    this.ready = false;
    try { this.app.destroy(true, { children: true, texture: true }); } catch { /* */ }
    document.getElementById('pixi-bg')?.remove();
  }
}
